import { useEffect, useMemo, useState } from "react";
import {
  getCurrentPenalty,
  getCurrentTurn,
  getGameLobby,
  getMyGameStats,
  isGameGuestUnauthorized,
} from "../features/game/gameApi";
import { clearGameSession } from "../features/game/gameSession";

function resolveStage({ turn, penalty, liveState }) {
  if (
    penalty?.isOrphanedLiveState ||
    liveState?.recoveryFlags?.adminAttentionRequired
  ) {
    return "waiting_recovery";
  }

  if (!turn) return "idle_lobby";

  if (turn.status === "answer_pending") return "answer_stage";
  if (turn.status === "guess_pending") return "guess_stage";
  if (turn.status === "result_ready") return "result_stage";
  if (turn.status === "penalty_pending") return "penalty_choice";
  if (turn.status === "dare_active") return "dare_stage";
  return "idle_lobby";
}

export default function useGameRecovery({
  gameId,
  gameToken,
  navigate,
  includeStats = true,
}) {
  const [state, setState] = useState({
    loading: true,
    error: "",
    lobby: null,
    currentTurn: null,
    penalty: null,
    myStats: null,
    stage: "idle_lobby",
    recoveryStatus: "ready",
    shouldRedirect: false,
    redirectTo: "",
  });

  useEffect(() => {
    let mounted = true;

    const run = async () => {
      if (!gameId || !gameToken) {
        if (mounted) {
          setState((prev) => ({
            ...prev,
            loading: false,
            shouldRedirect: true,
            redirectTo: "/game/enter",
            recoveryStatus: "stale_session",
          }));
        }
        return;
      }

      try {
        setState((prev) => ({ ...prev, recoveryStatus: "reconnecting" }));

        const [lobby, currentTurn, penalty, myStats] = await Promise.all([
          getGameLobby(gameId, gameToken),
          getCurrentTurn(gameId, gameToken),
          getCurrentPenalty(gameId, gameToken),
          includeStats
            ? getMyGameStats(gameId, gameToken)
            : Promise.resolve(null),
        ]);

        const stage = resolveStage({
          turn: currentTurn?.turn,
          penalty,
          liveState: lobby?.liveState || currentTurn?.liveState,
        });

        if (!mounted) return;

        const waitingAdmin =
          lobby?.liveState?.recoveryFlags?.adminAttentionRequired ||
          penalty?.isOrphanedLiveState;

        setState({
          loading: false,
          error: "",
          lobby,
          currentTurn,
          penalty,
          myStats: myStats?.stats || null,
          stage,
          recoveryStatus: waitingAdmin ? "waiting_admin" : "recovered",
          shouldRedirect: false,
          redirectTo: "",
        });

        setTimeout(() => {
          if (!mounted) return;
          setState((prev) => ({ ...prev, recoveryStatus: "ready" }));
        }, 1000);
      } catch (error) {
        if (!mounted) return;

        if (isGameGuestUnauthorized(error)) {
          clearGameSession();
          setState((prev) => ({
            ...prev,
            loading: false,
            error: "Guest session expired",
            recoveryStatus: "stale_session",
            shouldRedirect: true,
            redirectTo: "/game/enter",
          }));
          navigate?.("/game/enter", { replace: true });
          return;
        }

        setState((prev) => ({
          ...prev,
          loading: false,
          recoveryStatus: "waiting_admin",
          error: error?.response?.data?.message || "Recovery failed",
        }));
      }
    };

    run();
    return () => {
      mounted = false;
    };
  }, [gameId, gameToken, includeStats, navigate]);

  return useMemo(() => state, [state]);
}
